<template>
    <div
        ref="graph-container"
        class="graph-modelisation"
    >
        <svg
            id="svg-canvas"
            :width="svgWidth"
            :height="height"
        />
        <v-speed-dial
            v-model="dialBtn"
            open-on-click
            class="tool-btn"
            right
            bottom
            direction="top"
        >
            <template v-slot:activator>
                <v-btn
                    v-model="dialBtn"
                    color="grey darken-4"
                    small
                    dark
                    fab
                >
                    <v-icon v-if="dialBtn" style="display: inline-flex;">close</v-icon>
                    <v-icon  v-else style="display: inline-flex;">build</v-icon>
                </v-btn>
            </template>
            <v-tooltip left>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        fab
                        dark
                        small
                        @click="resetZoom"
                        color="grey darken-3"
                    >
                        <v-icon style="align-items: center; justify-content: center; display: flex;">zoom_in</v-icon>
                    </v-btn>
                </template>
                <span>
                    {{$t('instance.data_model.graph_modelisation.reset_zoom')}}
                </span>
            </v-tooltip>
            <v-tooltip left>
                <template v-slot:activator="{ on }">
                    <v-btn
                        v-on="on"
                        fab
                        dark
                        small
                        @click="downloadSvg"
                        color="grey darken-3"
                    >
                        <v-icon style="align-items: center; justify-content: center; display: flex;">save</v-icon>
                    </v-btn>
                </template>
                <span>
                    {{$t('instance.data_model.graph_modelisation.export_graph')}}
                </span>
            </v-tooltip>
        </v-speed-dial>
        <dialog-notify ref="data-model-error" title="Data model error">
            <p slot="content">
                {{$t('instance.data_model.graph_modelisation.errors_current_data_model')}}
            </p>
        </dialog-notify>
    </div>
</template>
<script>
import Dagre from 'dagre-d3';
import * as d3 from 'd3';
import { saveAs } from 'file-saver';
import DialogNotify from '@/components/Partials/dialog-notify.vue';
import { filter, includes, cloneDeep} from 'lodash';
import { mapGetters } from 'vuex';
export default {
    components: { DialogNotify },
    props: {
        schema: {
            required: true,
        },
        height: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            svgWidth: 0,
            dialBtn: false,
            zoomIdentity: null,
            filterTooltipActivated: false,
            idTimeoutFilterTooltip: null,
            svgGraph: null,
            zoom: null,
        };
    },
    computed: {
        relations (){
            return this.dataModeling.many_to_many_relations.concat(
                this.dataModeling.one_to_many_relations,
                // this.dataModeling.one_to_one_relations,
            );
        },

        dataModeling (){
            try{
                return this.schema.schema.manifest.data_modeling;
            }catch(e){
                return {};
            }
        },
        ...mapGetters([
            'modelHasError',
            'fieldInModelHasError',
            'modelsWithError',
        ]),

        scopeModel (){
            return this.schema.scopeModel;
        },

        allScopedModelUid (){
            return this.schema.allScopedModel.map((model) => model.model_uid );
        }
    },
    watch: {
        'schema.schema': {
            handler() {
                this.buildGraph(this.dataModeling);
            },
            immediate: true,
            deep: true,
        },

        modelsWithError: {
            handler (){
                this.buildGraph();
            },
            deep: true,
            immediate: true,
        },
    },
    mounted() {
        this.buildGraph(this.dataModeling);
    },
    methods: {
        buildGraph(schema) {
            let d = d3.select('.graph-modelisation');
            d3.select('#svg-canvas').remove();
            let svg = d.append('svg');
            this.$set(this, 'svgGraph', svg);
            if (this.$refs['graph-container']) {
                this.svgWidth = this.$refs['graph-container'].offsetWidth;
            }
            svg.attr('width', this.svgWidth)
                .attr('id', 'svg-canvas')
                .attr('height', this.height)
                .attr('style', 'background: #191919');
            let inner = svg.append('g');
            if (!schema) schema = this.dataModeling;
            if(Object.keys(schema).length === 0) return;
            let g = new Dagre.graphlib.Graph()
                .setGraph({})
                .setDefaultEdgeLabel(function() {
                    return {};
                });
            for(let model of schema.models){
                let modelName = model.name;
                let modelUid = model.uid;
                g.setNode(modelUid, {
                    labelType: 'html',
                    label: this.htmlTemplate(model, model.fields),
                    padding: 0,
                    border: 'none',
                });
                let targets = this.getRel(model.uid, model.fields);
                if (targets.length > 0) {
                    for (let t of targets) {
                        g.setEdge(
                            modelUid,
                            schema.models.find(model => model.uid === t.uid).uid,
                            {
                                style: 'stroke: #bdbdbd; fill: none;',
                                arrowheadStyle: 'fill: #bdbdbd; stroke: none;',
                            }
                        );
                    }
                }
            }
            let zoom = d3.zoom().on('zoom', ()=> {
                inner.attr('transform', d3.event.transform);
                inner.attr('cursor', 'move');
                this.zoomIdentity = cloneDeep(d3.event.transform);
            });
            this.$set(this, 'zoom', zoom);
            svg.call(zoom);
            /* styling */
            let render = new Dagre.render();

            inner.call(render, g);
            svg.selectAll('.path').attr('stroke', '#fff')
                .attr('stroke-width', '1.5px');
            svg.selectAll('rect').attr('stroke', '#999')
                .attr('fill', '#fff')
                .attr('stroke-width', '1.5px');
            svg.selectAll('text').attr('font-weight', '300')
                .attr('font-family', '"Helvetica Neue", Helvetica, Arial, sans-serif')
                .attr('font-size', '14px');
            // add event click on node for choose a model to edit
            let selections = inner.selectAll('g.node');
            selections.on('click', (d) => {
                if(this.schema.schemaHasError){
                    // pass
                    this.$refs['data-model-error'].open();
                    return;
                }
                this.$emit('save-previous-model');
                this.schema.setSelectedModelUid(d);
            });
            if(this.zoomIdentity){
                d3.zoomIdentity.x = this.zoomIdentity.x;
                d3.zoomIdentity.y = this.zoomIdentity.y;
                d3.zoomIdentity.k = this.zoomIdentity.k;
                inner.attr('transform', () => {
                    return `translate(${this.zoomIdentity.x}, ${this.zoomIdentity.y}) scale(${this.zoomIdentity.k})`;
                });
            }

            this.addFilterEvents();
        },


        htmlTemplate(model, props) {
            let modelHasError = this.modelHasError(model.uid);
            let borderColor = modelHasError ? '3px solid red' : '1px solid #cacaca;';
            let html = `<div class="model" style="border: ${borderColor}">
                <div class="header">`;
            if(model.uid === this.scopeModel.uid){
                html += '<div class="graph-scope-model"><svg class="svg-filter" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M487.976 0H24.028C2.71 0-8.047 25.866 7.058 40.971L192 225.941V432c0 7.831 3.821 15.17 10.237 19.662l80 55.98C298.02 518.69 320 507.493 320 487.98V225.941l184.947-184.97C520.021 25.896 509.338 0 487.976 0z" stroke="none"></path></svg></div>';
            }else if(this.allScopedModelUid.includes(model.uid)){

                html += '<div class="graph-model-scoped"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="svg-filter"><path d="M479.968 0H32.038C3.613 0-10.729 34.487 9.41 54.627L192 237.255V424a31.996 31.996 0 0 0 10.928 24.082l64 55.983c20.438 17.883 53.072 3.68 53.072-24.082V237.255L502.595 54.627C522.695 34.528 508.45 0 479.968 0zM288 224v256l-64-56V224L32 32h448L288 224z" fill="#000"></path></svg></div>';
            }
            html += `
                    ${model.name}
                </div>
                <div class="content">`;
            let propsNames = props.map((p) => p.name);
            let duplicates = filter(propsNames, (value, index, iteratee) => includes(iteratee, value, index + 1));
            for (let item of props){
                let color = '#000';
                let fieldHasError = this.fieldInModelHasError({modelUid: model.uid, field: item.name});

                if(fieldHasError || duplicates.includes(item.name)){
                    color = 'red';
                }
                html += `<div style="color: ${color};" class="${fieldHasError ? 'field-error' : ''}"> ${item.name} (${item.datatype})</div>`;
            }
            html += `</div>
            </div>`;
            return html;
        },



        addFilterEvents (){
            if(this.scopeModel.uid === null) return;
            let tooltip = document.querySelector('.graph-scope-model-tooltip');
            document.querySelector('.graph-scope-model').addEventListener('mouseover', (event) => {
                if(this.filterTooltipActivated){
                    clearTimeout(this.idTimeoutFilterTooltip);
                    return;
                }
                this.filterTooltipActivated = true;
                tooltip.innerHTML = `<p>${this.t('instance.data_model.graph_modelisation.addFilterEvents_scope_model')}<ul>`;
                for(let model of this.schema.allScopedModel){
                    tooltip.innerHTML += `<li>${model.model_name}</li>`;
                }
                tooltip.innerHTML += '</ul></p>';
                tooltip.style.visibility = 'visible';
                let tooltipRect = tooltip.getBoundingClientRect();

                let someSpace = 30;
                tooltip.style.top = (event.pageY - someSpace - tooltipRect.height)+'px';
                tooltip.style.left = (event.pageX - tooltipRect.width/2)+'px';

            });
            document.querySelector('.graph-scope-model').addEventListener('mouseout', () => {
                // sinon le tooltip avait tendence à flicker
                this.idTimeoutFilterTooltip = setTimeout(() => {
                    tooltip.style.visibility = 'hidden';
                    this.filterTooltipActivated = false;
                }, 50);
            });
            let graphScopeModel = document.querySelectorAll('.graph-model-scoped');
            for(let elem of graphScopeModel){
                elem.addEventListener('mouseover', (event) => {
                    if(this.filterTooltipActivated){
                        clearTimeout(this.idTimeoutFilterTooltip);
                        return;
                    }
                    this.filterTooltipActivated = true;
                    tooltip.innerHTML = `<p>${this.t('instance.data_model.graph_modelisation.addFilterEvents_graph_scope_model')} ${this.scopeModel.name}`;
                    tooltip.style.visibility = 'visible';
                    let tooltipRect = tooltip.getBoundingClientRect();

                    let someSpace = 30;
                    tooltip.style.top = (event.pageY - someSpace - tooltipRect.height)+'px';
                    tooltip.style.left = (event.pageX - tooltipRect.width/2)+'px';

                });
                elem.addEventListener('mouseout', () => {
                    // sinon le tooltip avait tendence à flicker
                    this.idTimeoutFilterTooltip = setTimeout(() => {
                        tooltip.style.visibility = 'hidden';
                        this.filterTooltipActivated = false;
                    }, 50);
                });
            }
        },
        getRel(modelUid, props) {
            let relationFromModel = props.filter((prop) => {
                return ['fk', 'm2m'].includes(prop.datatype); //'o2o',
            });

            let allRelationsFilteredByCurrentModel = this.relations.filter((rel) => {
                return rel.source_model.uid === modelUid;
            });

            let rels = [];
            for (let modelRelation of relationFromModel) {
                for (let targetRelation of allRelationsFilteredByCurrentModel) {
                    if(modelRelation.attributes.hasOwnProperty('to') &&
                       modelRelation.attributes.to.uid === targetRelation.target_model.uid
                    ){
                        rels.push(targetRelation.target_model);
                    }
                }
            }

            return rels;
        },
        resetZoom() {
            d3.zoomIdentity.x = 0;
            d3.zoomIdentity.y = 0;
            d3.zoomIdentity.k = 1;
            this.zoomIdentity = null;
            this.buildGraph();
        },

        downloadSvg() {

            let svg = d3.select('#svg-canvas');
            let g = d3.select('g');
            let svgSize = {
                width: svg.style('width'),
                height: svg.style('height')
            };

            let gPosition = g.attr('transform');

            //needed for all element to fit svg on export
            let contentPosition = g.node().getBBox();
            let offset = Math.abs(contentPosition.x) + 10;

            g.attr('transform', `translate(${offset},0) scale(1)`);
            let computedSize = svg.node().getBBox();
            svg.style('width', computedSize.width + offset + 'px')
                .style('height', computedSize.height +'px');
            var svgString = this.getSVGString(d3.select('#svg-canvas').node());

            let regex = new RegExp(`${window.location.origin}/`, 'g');
            console.warn('A MODIFIER EN PROD (OU PAS?)');
            svgString = svgString.replace(regex, '');
            var blob = new Blob([svgString], {type: 'image/svg+xml;charset=utf-8'});
            saveAs(blob, 'file.svg');

            svg.style('width', svgSize.width + 'px')
                .style('height', svgSize.height + 'px');
            g.attr('transform', gPosition);
        },
        getSVGString(svgNode) {
            svgNode.setAttribute('xlink', 'http://www.w3.org/1999/xlink');
            var cssStyleText = getCSSStyles(svgNode);
            appendCSS(cssStyleText, svgNode);
            let foreignObjects = svgNode.querySelectorAll('foreignObject');
            for(let item of foreignObjects){
                item.setAttribute('height', parseInt(item.getAttribute('height')) + 10);
            }
            var serializer = new XMLSerializer();
            var svgString = serializer.serializeToString(svgNode);
            svgString = svgString.replace(/(\w+)?:?xlink=/g, 'xmlns:xlink='); // Fix root xlink without namespace
            svgString = svgString.replace(/NS\d+:href/g, 'xlink:href'); // Safari NS namespace fix

            return svgString;

            function getCSSStyles(parentElement) {
                var selectorTextArr = [];

                // Add Parent element Id and Classes to the list
                selectorTextArr.push('#' + parentElement.id);
                for (var c = 0; c < parentElement.classList.length; c++)
                    if (!contains('.' + parentElement.classList[c], selectorTextArr))
                        selectorTextArr.push('.' + parentElement.classList[c]);

                    // Add Children element Ids and Classes to the list
                var nodes = parentElement.getElementsByTagName('*');
                for (var i = 0; i < nodes.length; i++) {
                    var id = nodes[i].id;
                    if (!contains('#' + id, selectorTextArr))
                        selectorTextArr.push('#' + id);

                    var classes = nodes[i].classList;
                    for (var c = 0; c < classes.length; c++)
                        if (!contains('.' + classes[c], selectorTextArr))
                            selectorTextArr.push('.' + classes[c]);
                }

                // Extract CSS Rules
                var extractedCSSText = '';
                for (var i = 0; i < document.styleSheets.length; i++) {
                    var s = document.styleSheets[i];

                    try {
                        if (!s.cssRules) continue;
                    } catch (e) {
                        if (e.name !== 'SecurityError') throw e; // for Firefox
                        continue;
                    }

                    var cssRules = s.cssRules;
                    for (var r = 0; r < cssRules.length; r++) {
                        if (contains(cssRules[r].selectorText, selectorTextArr))
                            extractedCSSText += cssRules[r].cssText;
                    }
                }
                extractedCSSText += '.model{background-color:#fff;border-radius:3px;border:1px solid #cacaca;}.model .header{background-color:#ececec;border-bottom:1px solid #cacaca;text-align:center;font-weight:bold;padding:10px;color:#000;}.model .header .svg-filter{width:14px;height:11px;display:flex;margin-left:auto;}.model .content{padding:0 10px;}.model .content div{margin:10px 0;padding:0 10px 10px 15px;}.model .content div:not(:last-child){border-bottom:1px solid #cacaca;}.model .content div:last-child{margin-bottom:0;}';
                return extractedCSSText;

                function contains(str, arr) {
                    return arr.indexOf(str) === -1 ? false : true;
                }

            }

            function appendCSS(cssText, element) {
                var styleElement = document.createElement('style');
                styleElement.setAttribute('type', 'text/css');
                styleElement.innerHTML = cssText;
                var refNode = element.hasChildNodes() ? element.children[0] : null;
                element.insertBefore(styleElement, refNode);
            }
        },

        focusNode (nodeUid){
            let node = d3.selectAll('g.node').filter(function(d) { return d === nodeUid; }).node();
            const matrix = node.transform.baseVal.consolidate().matrix;
            this.svgGraph.transition().duration(750)
                .call(this.zoom.translateTo, matrix.e, matrix.f);
        }
    }
};

</script>
<style lang="scss">
.graph-modelisation {
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    position: relative;
    background: black;
    text-align: center;
    width: 60%;

    .tool-btn {
        position: absolute;
        // bottom: 15px;
        // right: 15px;
    }

    .node {
        cursor: pointer;
        rect {
            stroke: none;
        }
    }

    .class-item {
        width: 200px;

        .model-name-container {
            color: #000;
            line-height: 1.8em;
        }

        .model-prop-container {
            color: #000;
            line-height: 1.4em;
        }
    }
    .model{
        background-color: #fff;
        border: 1px solid #cacaca;
        position: relative;
        font-size: 14px;
        .header{
            background-color: #ececec;
            border-bottom: 1px solid #cacaca;
            text-align: center;
            font-weight: bold;
            padding: 10px;
            color: #000;
            .svg-filter{
                width: 14px;
                height: 11px;
                display: flex;
                margin-left: auto;
            }
        }
        .content{
            padding: 0 10px;
            div{
                margin: 10px 0;
                padding: 0 10px 10px 15px;
                &:hover{
                    color: #424242 !important;
                }
            }
            div:not(:last-child){
                border-bottom: 1px solid #cacaca;
            }
            div:last-child{
                margin-bottom: 0;
            }
            .field-error{
                color: red;
                text-decoration: underline;
            }
        }
    }


}


</style>



// WEBPACK FOOTER //
// src/components/page/Instance/DataModel/graph-modelisation.vue